import LocaleOptions from '@scoring-web/configs/locale/locale.type';
import {
  PersistedStoreState,
  StoreConfig,
  basePersistenceStoreKey,
} from '@scoring-web/configs/store/store.config';
import { getStoreBaseActions, getStoreHydrationActions } from '@scoring-web/utils/store.util';
import { Query, QueryKey } from '@tanstack/react-query';
import { proxy, useSnapshot } from 'valtio';
import { subscribeKey } from 'valtio/utils';

// #region Types
export type QueryError = {
  error: Error;
  queryKey?: QueryKey;
  time: number;
};

type MainState = PersistedStoreState & {
  queryErrors: QueryError[];
  selectedLanguage: LocaleOptions;
};
// #endregion

// #region State
const initial = {
  queryErrors: [],
  selectedLanguage: LocaleOptions.JA,
} satisfies MainState;
const state = proxy<MainState>({ ...initial });
// #endregion

// #region Snapshot
export function useMainStore() {
  return useSnapshot(state);
}
// #endregion

// #region Constants
const persistenceKey = `${basePersistenceStoreKey}.main-selected-language`;
// #endregion

// #region Actions
const actions = {
  ...getStoreBaseActions(state, initial),
  ...getStoreHydrationActions(persistenceKey, state, 'selectedLanguage'),
  addError: (errorObject: unknown, query?: Query) => {
    let isValid = errorObject instanceof Error;
    isValid ||=
      typeof errorObject === 'object' && !!errorObject && Object.hasOwn(errorObject, 'message');
    isValid &&= !!errorObject;

    if (!isValid) return;

    const error = errorObject as Error;
    state.queryErrors = [
      ...state.queryErrors.filter(({ error: { message } }) => message !== error.message),
      { error, queryKey: query?.queryKey, time: Date.now() },
    ];
  },
  changeLanguage: (locale: LocaleOptions) => {
    state.selectedLanguage = locale;
  },
  setQueryErrors: (queryErrors: QueryError[]) => {
    state.queryErrors = queryErrors;
  },
};
// #endregion

// #region Store Initialization
actions.hydrate();
subscribeKey(state, 'selectedLanguage', () => actions.persist());
// #endregion

// #region Store
type MainStore = StoreConfig<MainState, typeof actions>;
const mainStore: MainStore = { actions, initial, state };
// #endregion

export default mainStore;
